import { Component, DestroyRef, effect, HostListener, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from '../../shared/services/local-storage/local-storage.service';
import { environment } from '../../../environments/environment';
import { AppSettings } from '../../shared/interfaces/app-settings.interface';
import { Router, RouterModule } from '@angular/router';
import { MenuService } from '../../shared/services/menu/menu.service';
import { normalizeLang, langToRoute, I18N_CONFIG, SupportedLang } from '../../shared/localization/i18n.config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    TranslatePipe,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
})
export class HeaderComponent {
  @Input() activeSection = "";
  isMobileMenuOpen = false;
  currentLanguage: SupportedLang;
  availableLanguages = I18N_CONFIG.supported;
  private destroyRef = inject(DestroyRef);

  navLinks = [
    { fragment: 'home', label: 'header.nav.home' },
    { fragment: 'about', label: 'header.nav.about' },
    { fragment: 'experience', label: 'header.nav.experience' },
    { fragment: 'education', label: 'header.nav.education' },
    { fragment: 'projects', label: 'header.nav.projects' },
    // { fragment: 'blog', label: 'header.nav.blog' },
    { fragment: 'contact', label: 'header.nav.contact' },
  ];
  
  constructor(
    private translate: TranslateService,
    private localStorageService: LocalStorageService,
    private menuService: MenuService,
    private router: Router
  ) {
    this.currentLanguage = normalizeLang(this.translate.currentLang || this.translate.defaultLang);
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      this.currentLanguage = normalizeLang(event.lang);
    });

    // Reacts to the signal emitted by the MenuService.
    // When shouldCloseMenu() becomes true, this will close the mobile menu.
    // The signal is automatically reset after triggering to allow future activations.
    effect(() => {
      if (this.menuService.shouldCloseMenu()) {
        this.isMobileMenuOpen = false;
      }
    });
  }

  getLanguageLabel(lang: SupportedLang): string {
    return I18N_CONFIG.labelMap[lang];
  }

  private updateCurrentLanguage(language: SupportedLang): void {
    const localStorageItem = this.localStorageService
      .getItem<AppSettings>(environment.localStorageKey.appSettings);

    if (localStorageItem?.language === language) {
      return;
    }

    const appSettings: AppSettings = localStorageItem || { language: I18N_CONFIG.defaultLang };
    appSettings.language = language;
    this.localStorageService
      .setItem(environment.localStorageKey.appSettings, appSettings);
  }

  switchLanguage(lang: SupportedLang): void {
    this.translate.use(lang);
    this.currentLanguage = lang;
    this.updateCurrentLanguage(lang);
    this.isMobileMenuOpen = false;
    const fragment = this.activeSection || 'home';
    this.router.navigate([this.getBasePath()], { fragment });
  }

  getBasePath(): string {
    return `/${langToRoute(normalizeLang(this.currentLanguage))}`;
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const mobileMenuToggle = document.getElementById('mobileMenuToggle');
    const mobileMenu = document.getElementById('mobileMenu');
    
   if (mobileMenuToggle && !mobileMenuToggle.contains(target) && 
      mobileMenu && !mobileMenu.contains(target)) {
      this.isMobileMenuOpen = false;
   }
  }
}

